import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, errMsg, makeIdem, ApiError, type Account, type Reconciliation, type Statement, type StatementEntry } from "../../api";
import { HistoryModal } from "../../components/HistoryModal";
import { Modal } from "../../components/Modal";
import { Btn, Field, Input, Notice, Select } from "../../components/ui";
import { fmtDay, formatMoney } from "../../lib/money";
import { usePeriod } from "../../period";
import { useTxFlow } from "../../txflow";

const LIMIT = 50;
type Msg = { kind: "ok" | "error"; text: string };

/** Linha de movimentação da conta, normalizada só para exibição: tudo (valor, sinal, situação, retroativo) vem pronto do servidor. */
export interface Move {
  key: string; sourceType: "transaction" | "transfer"; sourceId: string; leg: "saida" | "entrada" | null; date: string; title: string; sub: string | null;
  amountDecimal: string; currency: "BRL" | "USD"; negative: boolean; reconciled: boolean; reconciliationId: string | null; retroactive: boolean;
}
export function toMove(l: StatementEntry, fallbackCurrency: "BRL" | "USD"): Move {
  if (l.entryType === "transferencia") {
    const out = l.leg === "saida";
    return {
      key: `t-${l.id}-${l.leg}`, sourceType: "transfer", sourceId: l.id, leg: l.leg, date: l.settledDate ?? l.plannedDate,
      title: `${out ? "Transferência para" : "Transferência de"} ${l.counterAccountName}`, sub: l.description,
      amountDecimal: l.legAmount.decimal, currency: out ? l.fromCurrency : l.toCurrency, negative: out,
      reconciled: !!l.reconciled, reconciliationId: l.reconciliationId ?? null, retroactive: !!l.retroactive,
    };
  }
  return {
    key: `x-${l.id}`, sourceType: "transaction", sourceId: l.id, leg: null, date: l.settledDate ?? l.plannedDate, title: l.description, sub: l.counterpartyName,
    amountDecimal: l.amount.decimal, currency: l.currency ?? fallbackCurrency, negative: l.kind === "despesa",
    reconciled: !!l.reconciled, reconciliationId: l.reconciliationId ?? null, retroactive: !!l.retroactive,
  };
}
export const MoveAmount = ({ m }: { m: Move }) => <span className={`tabular ${m.negative ? "neg" : "pos"}`}>{m.negative ? "−" : "+"} {formatMoney(m.amountDecimal, m.currency)}</span>;

/** Conciliação MANUAL (DEC-095): sem extrato bancário, sem CSV e sem linha. O servidor decide a elegibilidade; a tela só confirma o que ele aceitou. */
export function Moves({ realm, account, canWrite }: { realm: string; account: Account; canWrite: boolean }) {
  const flow = useTxFlow();
  const per = usePeriod();   // o período é o do seletor de mês do cabeçalho (fonte única; sem filtro de datas próprio)
  const { from, to } = per;
  const [sit, setSit] = useState<"pendentes" | "conciliadas" | "todas">("pendentes");
  const [offset, setOffset] = useState(0);
  const [st, setSt] = useState<Statement | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [msg, setMsg] = useState<Msg | null>(null);
  const [dlg, setDlg] = useState<{ kind: "match" | "undo" | "history"; m: Move } | null>(null);

  const seq = useRef(0);   // só a resposta da requisição mais recente vale (evita lista antiga sobrescrever a nova)
  const load = useCallback(async () => {
    const my = ++seq.current;
    const qs = new URLSearchParams({ from, to, limit: String(LIMIT), offset: String(offset) });
    if (sit !== "todas") qs.set("reconciled", sit === "conciliadas" ? "true" : "false");
    try { const r = await api<Statement>("GET", `/api/${realm}/accounts/${account.id}/statement?${qs}`); if (my === seq.current) { setSt(r); setState("ok"); } } catch { if (my === seq.current) setState("error"); }
  }, [realm, account.id, from, to, sit, offset]);
  useEffect(() => { setState("loading"); void load(); }, [load, flow.version]);
  useEffect(() => { setOffset(0); }, [account.id, from, to, sit]);

  const rows = (st?.lines ?? []).map((l) => toMove(l, account.currency));
  return (
    <>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <p className="muted small" style={{ marginTop: 0 }}>Conciliar confirma que a movimentação confere com o seu banco. <strong>Não altera saldos</strong> e não exige extrato, arquivo ou linha bancária.</p>
      <div className="toolbar filters">
        <Field label="Situação"><Select value={sit} onChange={(e) => setSit(e.target.value as typeof sit)} name="moveSit"><option value="pendentes">Pendentes de conciliação</option><option value="conciliadas">Conciliadas</option><option value="todas">Todas</option></Select></Field>
        <div className="small muted" style={{ alignSelf: "end", paddingBottom: 8 }} data-move-period>Período: {fmtDay(from)} a {fmtDay(to)} (muda no seletor de mês do cabeçalho)</div>
      </div>
      {state === "error" && <Notice kind="error">Não foi possível carregar as movimentações. Tente novamente.</Notice>}
      {st && rows.length === 0 && state === "ok" && <div className="card stub"><h3>{sit === "pendentes" ? "Nenhuma movimentação pendente" : "Nenhuma movimentação"}</h3><p className="muted" style={{ margin: 0 }}>Só movimentações efetivadas (pagas, recebidas ou realizadas) podem ser conciliadas.</p></div>}
      {st && rows.length > 0 && (
        <div className="card table-card">
          <table aria-label="Movimentações a conciliar" data-moves>
            <thead><tr><th>Data</th><th>Descrição</th><th className="num">Valor</th><th>Situação</th><th aria-label="Ações" /></tr></thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.key} data-move={m.title}>
                  <td className="tabular">{fmtDay(m.date)}{m.retroactive && <div><span className="pill accent" data-badge-retro title="Lançada depois, com data anterior a hoje">Retroativo</span></div>}</td>
                  <td><strong>{m.title}</strong>{m.sub && <div className="muted small">{m.sub}</div>}</td>
                  <td className="num"><MoveAmount m={m} /></td>
                  <td><span className={`pill${m.reconciled ? " recon" : ""}`} data-recon-state={m.reconciled ? "conciliado" : "pendente"}>{m.reconciled ? "Conciliado" : "Pendente"}</span></td>
                  <td><span className="actions">
                    {canWrite && !m.reconciled && <button className="link" onClick={() => setDlg({ kind: "match", m })} data-act="reconcile">Conciliar</button>}
                    {canWrite && m.reconciled && m.reconciliationId && <button className="link danger" onClick={() => setDlg({ kind: "undo", m })} data-act="unreconcile">Desfazer conciliação</button>}
                    {m.reconciled && m.reconciliationId && <button className="link" onClick={() => setDlg({ kind: "history", m })} data-act="recon-history">Histórico</button>}
                  </span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pager small muted">
            {st.total} {st.total === 1 ? "movimentação" : "movimentações"}
            <span className="spacer" />
            <Btn variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Anteriores</Btn>
            <Btn variant="ghost" disabled={offset + LIMIT >= st.total} onClick={() => setOffset(offset + LIMIT)}>Próximos</Btn>
          </div>
        </div>
      )}
      {dlg?.kind === "match" && <ManualDialog realm={realm} m={dlg.m} onClose={() => setDlg(null)} onDone={(text) => { setDlg(null); setMsg({ kind: "ok", text }); void load(); flow.bump(); }} />}
      {dlg?.kind === "undo" && <UndoDialog realm={realm} reconciliationId={dlg.m.reconciliationId!} title={dlg.m.title} onClose={() => setDlg(null)} onDone={(text) => { setDlg(null); setMsg({ kind: "ok", text }); void load(); flow.bump(); }} onError={(text) => { setDlg(null); setMsg({ kind: "error", text }); void load(); }} />}
      {dlg?.kind === "history" && <HistoryModal title={`Histórico da conciliação — ${dlg.m.title}`} path={`/api/${realm}/reconciliations/${dlg.m.reconciliationId}/history`} onClose={() => setDlg(null)} />}
    </>
  );
}

export function ManualDialog({ realm, m, onClose, onDone }: { realm: string; m: Move; onClose: () => void; onDone: (text: string) => void }) {
  const idem = useRef(makeIdem());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api<Reconciliation>("POST", `/api/${realm}/reconciliations/manual`, { sourceType: m.sourceType, sourceId: m.sourceId, ...(m.leg ? { leg: m.leg } : {}) }, { "idempotency-key": idem.current.get()["idempotency-key"] });
      onDone(`Conciliado (${r.origin === "MANUAL" ? "manual" : "assistida"}): ${m.title}. Nenhum saldo foi alterado.`);
    } catch (x) { if (x instanceof ApiError) idem.current.renew(); setErr(errMsg(x)); } finally { setBusy(false); }
  }
  return (
    <Modal title="Conciliar movimentação" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <p style={{ marginTop: 0 }}>Confirme que esta movimentação confere com o seu banco:</p>
        <div className="card" style={{ marginBottom: 12 }}>
          <strong>{m.title}</strong>
          <div className="muted small">{fmtDay(m.date)}{m.leg ? ` · ponta de ${m.leg === "saida" ? "saída" : "entrada"}` : ""}</div>
          <div style={{ marginTop: 6 }}><MoveAmount m={m} /></div>
        </div>
        <p className="muted small">Depois de conciliada, valor, datas, conta e tipo ficam travados. Para alterar, desfaça a conciliação com um motivo.</p>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Voltar</Btn><button type="submit" className="btn primary" disabled={busy} data-confirm-reconcile>{busy ? "Conciliando…" : "Confirmar conciliação"}</button></div>
      </form>
    </Modal>
  );
}

export function UndoDialog({ realm, reconciliationId, title, onClose, onDone, onError }: { realm: string; reconciliationId: string; title: string; onClose: () => void; onDone: (text: string) => void; onError: (text: string) => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      await api<Reconciliation>("POST", `/api/${realm}/reconciliations/${reconciliationId}/undo`, { reason });
      onDone(`Conciliação desfeita: ${title}. O motivo ficou registrado na auditoria.`);
    } catch (x) {
      if (x instanceof ApiError && (x.code === "reconciliation_period_concluded" || x.code === "reconciliation_already_undone" || x.code === "version_conflict")) onError(errMsg(x));
      else setErr(errMsg(x));
    } finally { setBusy(false); }
  }
  return (
    <Modal title="Desfazer conciliação" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <p style={{ marginTop: 0 }}>A movimentação <strong>{title}</strong> volta a ficar pendente de conciliação. Nada é apagado: o vínculo e o motivo permanecem no histórico.</p>
        <Field label="Motivo (mínimo de 3 caracteres)"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} required name="undoReason" autoComplete="off" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Voltar</Btn><button type="submit" className="btn primary" disabled={busy || reason.trim().length < 3} data-confirm-undo>{busy ? "Desfazendo…" : "Desfazer conciliação"}</button></div>
      </form>
    </Modal>
  );
}
